import { Modal } from 'bootstrap';
import DOMPurify from 'dompurify';
import { initializePrintCropEditor, positionedPrintCrop } from './printCropEditor';
import { createPrintCropPhoto } from './printCropPreview';
import '../css/cart-product-editor.css';

const filter = effect => Number(effect) === 2 ? 'sepia(100%)' : Number(effect) === 1 ? 'grayscale(100%)' : 'none';

export function initializeCartProductEditor(root = document) {
    const host = root.querySelector('[data-product-editor-host]');
    if (!host || host.dataset.initialized) return;
    host.dataset.initialized = 'true';
    root.addEventListener('keydown', event => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-product-editor-trigger][role=button]')) { event.preventDefault(); event.target.click(); }
    });
    let loading = false;
    root.addEventListener('click', async event => {
        const trigger = event.target.closest('[data-product-editor-trigger]');
        if (!trigger || loading || host.firstChild) return;
        event.preventDefault();
        loading = true;
        window.ShopActionState.setBusy(trigger, true);
        const error = trigger.parentElement.querySelector('[data-product-editor-load-error]');
        error.hidden = true;
        try {
            const response = await fetch(trigger.dataset.productEditorTrigger, { credentials: 'same-origin', cache: 'no-store' });
            if (!response.ok || response.redirected) throw new Error('Editor unavailable');
            host.replaceChildren(DOMPurify.sanitize(await response.text(), { RETURN_DOM_FRAGMENT: true }));
            const modal = host.querySelector('[data-product-editor]');
            if (!modal) throw new Error('Editor missing');
            const instance = new Modal(modal, { backdrop: 'static' });
            const dispose = bindEditor(modal);
            modal.addEventListener('hidden.bs.modal', () => {
                dispose(); instance.dispose(); host.replaceChildren(); trigger.focus({ preventScroll: true });
            }, { once: true });
            instance.show();
        } catch {
            host.replaceChildren(); error.hidden = false; error.focus();
        } finally { loading = false; window.ShopActionState.setBusy(trigger, false); }
    });
}

function bindEditor(modal) {
    const data = JSON.parse(modal.dataset.productEditor);
    const draft = { id: data.id, code: data.code, revision: data.revision, photoId: data.photoId,
        productId: data.productId, effect: data.effect, quantity: data.quantity, cropChanged: false,
        horizontalOffset: data.horizontalOffset, verticalOffset: data.verticalOffset, zoom: data.zoom };
    const money = new Intl.NumberFormat(data.culture || undefined, { style: 'currency', currency: data.currency });
    const error = modal.querySelector('[data-editor-error]');
    const title = modal.querySelector('[data-editor-title]');
    const back = modal.querySelector('[data-editor-back]');
    const cropHost = modal.querySelector('[data-editor-crop-host]');
    let busy = false, disposed = false, disposeCrop;
    const requests = new AbortController();
    function showError(message) { error.textContent = message; error.hidden = false; error.focus(); }
    function resetCrop() { draft.cropChanged = false; draft.horizontalOffset = 50; draft.verticalOffset = 0; draft.zoom = 1; }
    function render() {
        const photo = data.photos.find(item => item.id === draft.photoId);
        const product = data.products.find(item => item.id === draft.productId);
        const preview = modal.querySelector('[data-editor-preview]');
        const baseCrop = photo.crops[draft.productId];
        const crop = baseCrop ? positionedPrintCrop(baseCrop, draft.horizontalOffset, draft.verticalOffset, draft.zoom) : null;
        preview.innerHTML = createPrintCropPhoto(photo, ['Color', 'BlackWhite', 'Sepia'][draft.effect], crop, preview.dataset.imageLabel, false, product.grid).html;
        modal.querySelector('[data-editor-product-name]').textContent = product.name;
        modal.querySelector('[data-editor-product-details]').textContent = product.details;
        modal.querySelector('[data-editor-quantity-value]').textContent = draft.quantity;
        modal.querySelector('[data-editor-price]').textContent = money.format(product.price * draft.quantity);
        modal.querySelector('[data-editor-open="crop"]').hidden = !data.canCrop || !product.canCrop;
        modal.querySelectorAll('[name="editorEffect"]').forEach(input => { input.checked = Number(input.value) === draft.effect; });
        modal.querySelectorAll('[data-editor-quantity]').forEach(button => {
            button.disabled = busy || (Number(button.dataset.editorQuantity) < 0 ? draft.quantity <= 1 : draft.quantity >= 10);
        });
        modal.querySelectorAll('[data-editor-photo-id]').forEach(button => {
            const option = data.photos.find(item => item.id === button.dataset.editorPhotoId);
            button.hidden = !option.products.includes(draft.productId);
            button.setAttribute('aria-pressed', String(option.id === draft.photoId));
            button.querySelector('img').style.filter = filter(draft.effect);
        });
        modal.querySelectorAll('[data-editor-product-id]').forEach(button => {
            const option = data.products.find(item => item.id === button.dataset.editorProductId);
            button.hidden = !photo.products.includes(option.id);
            button.setAttribute('aria-pressed', String(option.id === draft.productId));
            button.querySelector('[data-editor-option-price]').textContent = money.format(option.price);
        });
    }
    function setBusy(value) {
        busy = value; modal.setAttribute('aria-busy', String(value));
        modal.querySelectorAll('button, input').forEach(control => {
            if (!control.closest('[data-print-crop-editor]')) control.disabled = value;
        });
        modal.querySelector('[data-editor-save]').setAttribute('aria-busy', String(value));
        modal.querySelector('[data-editor-spinner]').hidden = !value;
        render();
    }
    function screen(name) {
        error.hidden = true;
        modal.querySelectorAll('[data-editor-screen]').forEach(section => { section.hidden = section.dataset.editorScreen !== name; });
        back.hidden = name === 'main';
        modal.querySelector('[data-editor-footer]').hidden = name !== 'main';
        title.textContent = name === 'main' ? title.dataset.mainTitle : modal.querySelector(`[data-editor-screen="${name}"]`).dataset.title;
        modal.querySelector('.cart-editor-scroll').scrollTop = 0;
        title.focus({ preventScroll: true });
    }
    async function openCrop() {
        setBusy(true);
        error.hidden = true;
        try {
            const url = new URL(modal.dataset.cropUrl, window.location.href);
            for (const key of ['id', 'code', 'photoId', 'productId', 'effect']) url.searchParams.set(key, draft[key]);
            const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: requests.signal });
            if (!response.ok || response.redirected) throw new Error('Crop unavailable');
            const fragment = DOMPurify.sanitize(await response.text(), { RETURN_DOM_FRAGMENT: true });
            const form = fragment.querySelector('[data-print-crop-editor]');
            if (!form) throw new Error('Crop missing');
            const configuration = JSON.parse(form.dataset.printCropEditor);
            Object.assign(configuration, { horizontalOffset: draft.horizontalOffset, verticalOffset: draft.verticalOffset, zoom: draft.zoom });
            form.dataset.printCropEditor = JSON.stringify(configuration);
            // This is a local draft screen. Never post the standalone crop form.
            form.removeAttribute('action');
            const cropBody = form.querySelector('.modal-body');
            cropBody.prepend(...form.querySelectorAll('.modal-header p'));
            form.classList.remove('px-2', 'pt-3', 'pb-2');
            cropBody.classList.remove('mt-2');
            form.querySelector('#printCropInstructions').textContent = modal.dataset.cropInstructions;
            cropBody.insertBefore(form.querySelector('.print-crop-info'), form.querySelector('[data-crop-error]'));
            form.querySelector('.modal-header').remove();
            form.querySelectorAll('[data-bs-dismiss]').forEach(button => {
                button.removeAttribute('data-bs-dismiss'); button.addEventListener('click', leaveCrop);
            });
            const submit = form.querySelector('[type="submit"]');
            submit.lastChild.textContent = modal.dataset.cropSave;
            cropHost.replaceChildren(form);
            disposeCrop?.();
            disposeCrop = initializePrintCropEditor(cropHost, null, position => {
                Object.assign(draft, position, { cropChanged: true }); leaveCrop();
            });
            screen('crop');
        } catch (exception) { if (!disposed && exception.name !== 'AbortError') showError(modal.dataset.error); }
        finally { if (!disposed) setBusy(false); }
    }
    function leaveCrop() { disposeCrop?.(); disposeCrop = null; cropHost.replaceChildren(); screen('main'); render(); }
    back.addEventListener('click', leaveCrop);
    modal.addEventListener('hide.bs.modal', event => { if (busy) event.preventDefault(); });
    modal.addEventListener('change', event => {
        if (event.target.matches('[name="editorEffect"]')) { draft.effect = Number(event.target.value); render(); }
    });
    modal.addEventListener('click', async event => {
        const button = event.target.closest('button');
        if (!button || busy) return;
        if (data.isPackageItem && (button.dataset.editorOpen === 'products' || button.dataset.editorProductId || button.dataset.editorQuantity)) return;
        if (button.dataset.editorOpen) {
            if (button.dataset.editorOpen === 'crop') await openCrop(); else screen(button.dataset.editorOpen);
        } else if (button.dataset.editorPhotoId) {
            if (draft.photoId !== button.dataset.editorPhotoId) { draft.photoId = button.dataset.editorPhotoId; resetCrop(); }
            render(); screen('main');
        } else if (button.dataset.editorProductId) {
            if (draft.productId !== button.dataset.editorProductId) { draft.productId = button.dataset.editorProductId; resetCrop(); }
            render(); screen('main');
        } else if (button.dataset.editorQuantity) {
            draft.quantity = Math.max(1, Math.min(10, draft.quantity + Number(button.dataset.editorQuantity))); render();
        } else if (button.hasAttribute('data-editor-save')) {
            error.hidden = true; setBusy(true);
            let saved = false;
            try {
                const response = await fetch(modal.dataset.saveUrl, { method: 'POST', credentials: 'same-origin', signal: requests.signal,
                    headers: { 'Content-Type': 'application/json', 'RequestVerificationToken': modal.querySelector('[name="__RequestVerificationToken"]').value,
                        'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(draft) });
                if (!response.ok || response.redirected) {
                    showError(response.status === 409 ? modal.dataset.conflict : modal.dataset.error); return;
                }
                saved = true;
                window.location.reload();
            } catch (exception) { if (!disposed && exception.name !== 'AbortError') showError(modal.dataset.error); }
            finally { if (!disposed && !saved) setBusy(false); }
        }
    });
    render();
    return () => { disposed = true; requests.abort(); disposeCrop?.(); };
}
